import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { Show } from '@vimeo/bokeh';
import { PlayerWrapper } from '@vimeo/clip-ui/components/PlayerWrapper';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { useViewer } from '@vimeo/viewer';
import {
  NextClip,
  Player,
  PlayerLoadingView,
  PreviousClip,
  PrevNextButtonWrapper,
} from '@vimeo/viewer-showcase-ui/modules';

import { useGetAdjacentPlaylistVideo } from '../hooks/use-get-adjacent-video';
import { useGetInfiniteVideos, useGetPaginatedVideos } from '../hooks/use-get-playlist-videos';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useGetShowcaseVideo } from '../hooks/use-get-showcase-video';
import { useAlbumStore } from '../state/albumStore';
import { bpViewAutoplayedNextVideo } from '../utils/analytics';
import { findNextPlayableClip } from '../utils/helper';

interface IframePlayerModuleProps {
  bannerHeight?: string;
}

export const IframePlayerModule = ({ bannerHeight }: IframePlayerModuleProps) => {
  const viewer = useViewer();

  const router = useRouter();
  const { isSideBySideActive } = useLayout();
  const { nextClipData, prevClipData, selectedId, clipIndex } = useGetAdjacentPlaylistVideo();
  const { page, perPage, setPage } = useGetPaginatedVideos();

  // TODO: update this to a hook that returns the first video
  const { clips } = useGetInfiniteVideos(); // Only used to get first clip data, if not on first page it's a fresh call - we could make this call more efficient, useGetFirstVideo?
  const { loop, hideNav } = useGetShowcase();
  const continuousPlay = useAlbumStore((state) => state.continuousPlay);
  const { clip } = useGetShowcaseVideo(selectedId);

  const pageSize = perPage ?? 1;

  const onVideoNavigationClick = (link: string, direction: 'next' | 'prev') => {
    // First determine if we need to update the current page based on the navigation direction and clip index
    if (direction === 'prev' && page && clipIndex === 0) setPage(page - 1);
    if (direction === 'next' && page && clipIndex === pageSize - 1) setPage(page + 1);

    router.push(link);
  };

  /** Continuous Play and Loop */
  const firstClip = clips?.[0];

  let continuousPlayClipData = continuousPlay ? nextClipData : null;

  // If continuous play is enabled and the next clip is an unarchived live, find the next playable clip
  if (continuousPlay && continuousPlayClipData?.eventId) {
    continuousPlayClipData = findNextPlayableClip(continuousPlayClipData.clipId, clips);
  }

  // When both loop and continuous play are enabled, but no next clip, play the first clip
  // Otherwise, the continuous play clip data is `null` (or `undefined` when data is not ready)
  if (loop && continuousPlay && !continuousPlayClipData) {
    continuousPlayClipData = firstClip;
  }

  const continuousPlayLink = continuousPlayClipData?.link;

  // Player ended, navigate to continuous play link; add `autoplay` query but not showing to the browser bar
  const onEnded = useCallback(() => {
    if (!continuousPlayLink) return;
    // If the current clip is the last clip on the page, we need to load the next page
    if (page && clipIndex === pageSize - 1) setPage(page + 1);

    bpViewAutoplayedNextVideo(
      selectedId || '',
      viewer,
      nextClipData?.privacy?.view || '',
      continuousPlayLink,
    );
    router.push(`${continuousPlayLink}&autoplay=1`, continuousPlayLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuousPlayLink, router, page, clipIndex, setPage, pageSize]);

  /** When directly landing on this module, `clip` might be loading, `activeClipId` might need extra render due to router change */
  if (!selectedId || !clip) return <PlayerLoadingView bannerHeight={bannerHeight} />;

  return (
    <PlayerWrapper
      backgroundColor="#000"
      data-autoplay={continuousPlay ? '1' : '0'}
      globalNavigationHidden={hideNav}
      outerContainerHeight={bannerHeight}
    >
      <Player clip={clip} onEnded={onEnded} />

      {!isSideBySideActive ? (
        <Show above="md">
          <PrevNextButtonWrapper {...(!prevClipData && { justifyContent: 'flex-end' })}>
            {prevClipData ? (
              <PreviousClip
                dataHref={prevClipData.link}
                onClick={() => onVideoNavigationClick(prevClipData.link, 'prev')}
              />
            ) : null}

            {nextClipData ? (
              <NextClip
                dataHref={nextClipData.link}
                onClick={() => onVideoNavigationClick(nextClipData.link, 'next')}
              />
            ) : null}
          </PrevNextButtonWrapper>
        </Show>
      ) : null}
    </PlayerWrapper>
  );
};
